import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, CircleHelp, Download, FileSpreadsheet, Users } from 'lucide-react';
import * as XLSX from 'xlsx';
import { downloadParticipantsTemplate } from '../../../utils/participantsTemplate';
import { useTemplate } from '../../../assets/js/TemplateContext';
import { useLanguage } from '../../../assets/js/LanguageContext';
import FormField from '../../common/FormField';
import LimitExceededModal from '../CreateSurvey/LimitExceededModal';
import Modal from '../../common/Modal';
import { usePlanLimits } from '../../../utils/planLimits';
import SectionEyebrow from '../../common/SectionEyebrow';
import TemplateSteps from './TemplateSteps';
import '../../../assets/styles/ChooseSurvey.css';
import '../../../assets/styles/TemplateBuilder.css';

const todayValue = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const CreateTemplateStep0 = ({ onSurveyCreate, templateName, onBack }) => {
  const { t } = useLanguage();
  const { updateParticipantes, updateFechaInicio, updateFechaFin } = useTemplate();
  const today = todayValue();
  const [fechaInicio, setFechaInicio] = useState(today);
  const [fechaFin, setFechaFin] = useState('');
  const fileInputRef = useRef(null);
  const [emails, setEmails] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [errors, setErrors] = useState({});
  const [limitInfo, setLimitInfo] = useState(null);
  const checkPlanLimits = usePlanLimits();

  useEffect(() => {
    updateFechaInicio(today);
    // Solo al entrar: deja la fecha de hoy guardada aunque no se toque el campo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      if (!worksheet) {
        setErrors((prev) => ({ ...prev, file: t('excel_no_worksheets') }));
        return;
      }

      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const emailsExtraidos = jsonData.map((row) => row.Email).filter(Boolean);
      const telefonosExtraidos = jsonData.map((row) => row.Telefono).filter(Boolean);

      // Basta con una de las dos columnas: solo emails, solo teléfonos o ambos
      if (emailsExtraidos.length === 0 && telefonosExtraidos.length === 0) {
        setErrors((prev) => ({ ...prev, file: t('excel_no_email_phone') }));
        return;
      }

      // Cada fila es una persona: se cuenta la columna que más datos tenga
      setEmails(emailsExtraidos.length >= telefonosExtraidos.length ? emailsExtraidos : telefonosExtraidos);
      updateParticipantes({ emails: emailsExtraidos, telefonos: telefonosExtraidos });
      setErrors((prev) => ({ ...prev, file: undefined }));
    } catch (error) {
      console.error('Error al leer el archivo de participantes:', error);
      setErrors((prev) => ({ ...prev, file: t('excel_no_worksheets') }));
    } finally {
      event.target.value = '';
    }
  };

  const handleStartChange = (event) => {
    const value = event.target.value;
    setFechaInicio(value);
    updateFechaInicio(value);
    setErrors((prev) => ({ ...prev, start: undefined, end: undefined }));
  };

  const handleEndChange = (event) => {
    const value = event.target.value;
    setFechaFin(value);
    updateFechaFin(value);
    setErrors((prev) => ({ ...prev, end: undefined }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!fechaInicio.trim()) nextErrors.start = t('create_start_date_required');
    else if (fechaInicio < today) nextErrors.start = t('create_start_date_past');

    if (!fechaFin.trim()) nextErrors.end = t('create_end_date_required');
    else if (fechaInicio && fechaFin < fechaInicio) nextErrors.end = t('create_end_date_before_start');

    setErrors((prev) => ({ ...prev, ...nextErrors, start: nextErrors.start, end: nextErrors.end }));

    if (nextErrors.start || nextErrors.end) return;

    // Aviso del plan aqui, antes de pasar de pagina, para cambiar el Excel sin rehacer nada
    const exceeded = checkPlanLimits(emails.length);
    if (exceeded) {
      setLimitInfo(exceeded);
      return;
    }

    onSurveyCreate({ fechaInicio, fechaFin });
  };

  return (
    <div className="tp-choose tp-setup">
      <button type="button" className="tp-choose__back" onClick={onBack}>
        <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
        {t('common_back')}
      </button>

      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('template_configure_kicker')}</SectionEyebrow>
          <h1 className="tp-choose__title">{templateName}</h1>
          <p className="tp-choose__lead">{t('setup_lead')}</p>
        </div>
      </header>

      <TemplateSteps current={1} />

      <form className="tp-form tp-setup__form" onSubmit={handleSubmit}>
        <div className="tp-setup__dates">
          <FormField
            id="template-start"
            label={t('create_start_date')}
            type="date"
            value={fechaInicio}
            min={today}
            onChange={handleStartChange}
            error={errors.start}
            hint={t('setup_start_hint')}
          />
          <FormField
            id="template-end"
            label={t('create_end_date')}
            type="date"
            value={fechaFin}
            min={fechaInicio || today}
            onChange={handleEndChange}
            error={errors.end}
          />
        </div>

        <section className="tp-setup__participants">
          <div className="tp-setup__participants-head">
            <h2 className="tp-setup__subtitle">
              <Users size={16} strokeWidth={2} aria-hidden="true" />
              {t('create_participants')}
            </h2>
            <button type="button" className="tp-setup__info" onClick={() => setShowInfo(true)}>
              <CircleHelp size={15} strokeWidth={2} aria-hidden="true" />
              {t('setup_anonymity_link')}
            </button>
          </div>

          <p className="tp-setup__hint">{t('setup_participants_hint')}</p>

          <div className="tp-setup__import">
            <button type="button" className="btn btn--outline" onClick={() => fileInputRef.current.click()}>
              <FileSpreadsheet size={16} strokeWidth={2} aria-hidden="true" />
              {t('create_import_participants')}
            </button>
            <button type="button" className="btn btn--ghost tp-setup__template" onClick={() => downloadParticipantsTemplate(t)}>
              <Download size={16} strokeWidth={2} aria-hidden="true" />
              {t('participants_template_download')}
            </button>
            {emails.length > 0 && (
              <span className="tp-setup__count">
                {t('setup_participants_loaded').replace('{count}', emails.length)}
              </span>
            )}
          </div>

          {errors.file && <p className="tp-form__alert" role="alert">{errors.file}</p>}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx, .xls"
            className="tp-sr-only"
            tabIndex={-1}
          />
        </section>

        <div className="tp-setup__actions">
          <button type="submit" className="btn btn--primary">
            {t('setup_continue')}
            <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </form>

      <LimitExceededModal
        isOpen={Boolean(limitInfo)}
        limitInfo={limitInfo}
        onClose={() => setLimitInfo(null)}
        onEdit={() => setLimitInfo(null)}
      />

      <Modal
        open={showInfo}
        onOpenChange={setShowInfo}
        variant="info"
        title={t('create_anonymity_title')}
        description={t('create_anonymity_text')}
        confirmText={t('common_close')}
      />
    </div>
  );
};

export default CreateTemplateStep0;
